<template>
  <div class="nurse-form-container">
    <div class="container">
      <div class="header-section">
        <h1>{{ isEditing ? 'Edit Nurse' : 'Add New Nurse' }}</h1>
        <button 
          class="btn btn-secondary"
          @click="goBack"
        >
          <i class="fas fa-arrow-left"></i> Back
        </button>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>
      
      <!-- Form -->
      <div v-if="!loading" class="form-container">
        <form @submit.prevent="submitForm">
          <div class="card">
            <div class="card-header">
              <h5>Nurse Information</h5>
            </div>
            <div class="card-body">
              <!-- Personal Information Section -->
              <div class="row mb-4">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="name" class="form-label">Full Name <span class="required">*</span></label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="name" 
                      v-model="nurseData.name" 
                      :class="{'is-invalid': errors.name}"
                      required
                    >
                    <div v-if="errors.name" class="invalid-feedback">
                      {{ errors.name[0] }}
                    </div>
                  </div>
                  
                  <div class="mb-3">
                    <label for="email" class="form-label">Email Address <span class="required">*</span></label>
                    <input 
                      type="email" 
                      class="form-control" 
                      id="email" 
                      v-model="nurseData.email" 
                      :class="{'is-invalid': errors.email}"
                      required
                    >
                    <div v-if="errors.email" class="invalid-feedback">
                      {{ errors.email[0] }}
                    </div>
                  </div>
                  
                  <div class="mb-3" v-if="!isEditing">
                    <label for="password" class="form-label">Password <span class="required">*</span></label>
                    <input 
                      type="password" 
                      class="form-control" 
                      id="password" 
                      v-model="nurseData.password" 
                      :class="{'is-invalid': errors.password}"
                      required
                    >
                    <div v-if="errors.password" class="invalid-feedback">
                      {{ errors.password[0] }}
                    </div>
                  </div>
                </div>
                
                <div class="col-md-6">


                  <div class="mb-3">
                    <label for="mobile_number" class="form-label">Mobile Number</label>
                    <input
                        type="tel"
                        class="form-control"
                        id="mobile_number"
                        v-model="nurseData.mobile_number"
                        :class="{'is-invalid': errors.mobile_number}"
                    >
                    <div v-if="errors.mobile_number" class="invalid-feedback">
                      {{ errors.mobile_number[0] }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="department" class="form-label">Department <span class="required">*</span></label>
                    <select 
                      class="form-select" 
                      id="department" 
                      v-model="nurseData.department_id" 
                      :class="{'is-invalid': errors.department_id}"
                      required
                    >
                      <option value="" disabled>Select Department</option>
                      <option 
                        v-for="dept in departments" 
                        :key="dept.id" 
                        :value="dept.id"
                      >
                        {{ dept.name }}
                      </option>
                    </select>
                    <div v-if="errors.department_id" class="invalid-feedback">
                      {{ errors.department_id[0] }}
                    </div>
                  </div>
                  
                  <div class="mb-3">
                    <label for="joining_date" class="form-label">Joining Date <span class="required">*</span></label>
                    <input 
                      type="date" 
                      class="form-control" 
                      id="joining_date" 
                      v-model="nurseData.joining_date" 
                      :class="{'is-invalid': errors.joining_date}"
                      required
                    >
                    <div v-if="errors.joining_date" class="invalid-feedback">
                      {{ errors.joining_date[0] }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Additional Details Section -->
              <h6 class="mb-3">Additional Details</h6>
              <div class="row">
                <div class="col-md-4">
                  <div class="mb-3">
                    <label for="max_hours" class="form-label">Max Hours Per Week</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      id="max_hours" 
                      v-model="nurseData.max_hours_per_week" 
                      min="0"
                      max="168"
                      :class="{'is-invalid': errors.max_hours_per_week}"
                    >
                    <div v-if="errors.max_hours_per_week" class="invalid-feedback">
                      {{ errors.max_hours_per_week[0] }}
                    </div>
                  </div>
                </div>
                
                <div class="col-md-4">
                  <div class="mb-3">
                    <label for="min_rest" class="form-label">Min Rest Hours</label>
                    <input 
                      type="number" 
                      class="form-control" 
                      id="min_rest" 
                      v-model="nurseData.min_rest_hours" 
                      min="0"
                      max="24"
                      :class="{'is-invalid': errors.min_rest_hours}"
                    >
                    <div v-if="errors.min_rest_hours" class="invalid-feedback">
                      {{ errors.min_rest_hours[0] }}
                    </div>
                  </div>
                </div>
                
                <div class="col-md-4">
                  <div class="mb-3">
                    <label for="hourly_rate" class="form-label">Hourly Rate</label>
                    <input
                        type="number"
                        class="form-control"
                        id="hourly_rate"
                        v-model="nurseData.hourly_rate"
                        min="0"
                        step="0.01"
                        :class="{'is-invalid': errors.hourly_rate}"
                    >
                    <div v-if="errors.hourly_rate" class="invalid-feedback">
                      {{ errors.hourly_rate[0] }}
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                  <label for="status" class="form-label">Status</label>
                    <select 
                      class="form-select" 
                      id="status" 
                      v-model="nurseData.is_active"
                    >
                      <option :value="true">Active</option>
                      <option :value="false">Inactive</option>
                    </select>
                    <div class="form-text">
                      Current status: {{ nurseData.is_active ? 'Active' : 'Inactive' }}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Specializations Section -->
              <h6 class="mt-3 mb-3">Specializations</h6>
              <div class="mb-3">
                <div class="specialization-chips">
                  <div 
                    v-for="spec in selectedSpecializations" 
                    :key="spec.id" 
                    class="spec-chip"
                  >
                    {{ spec.name }}
                    <button 
                      type="button" 
                      class="btn-remove" 
                      @click="removeSpecialization(spec.id)"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div class="input-group">
                  <select 
                    class="form-select" 
                    v-model="selectedSpecId"
                  >
                    <option value="" disabled>Select Specialization</option>
                    <option 
                      v-for="spec in availableSpecializations" 
                      :key="spec.id" 
                      :value="spec.id"
                    >
                      {{ spec.name }}
                    </option>
                  </select>
                  <button 
                    type="button" 
                    class="btn btn-outline-primary" 
                    @click="addSpecialization"
                    :disabled="!selectedSpecId"
                  >
                    Add
                  </button>
                </div>
              </div>
              
              <!-- Notes Section -->
<!--              <div class="mb-3 mt-4">-->
<!--                <label for="notes" class="form-label">Notes</label>-->
<!--                <textarea -->
<!--                  class="form-control" -->
<!--                  id="notes" -->
<!--                  v-model="nurseData.notes" -->
<!--                  rows="3"-->
<!--                  :class="{'is-invalid': errors.notes}"-->
<!--                ></textarea>-->
<!--                <div v-if="errors.notes" class="invalid-feedback">-->
<!--                  {{ errors.notes[0] }}-->
<!--                </div>-->
<!--              </div>-->
            </div>
            <div class="card-footer">
              <div class="d-flex justify-content-end">
                <button 
                  type="button" 
                  class="btn btn-outline-secondary me-2" 
                  @click="goBack"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  class="btn btn-primary" 
                  :disabled="submitting"
                >
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  {{ isEditing ? 'Update Nurse' : 'Create Nurse' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NurseForm',
  props: {
    id: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      nurseData: {
        name: '',
        email: '',
        password: '',
        mobile_number: '',
        department_id: '',
        joining_date: '',
        max_hours_per_week: 40,
        min_rest_hours: 10,
        hourly_rate: '',
        is_active: true,
        notes: ''
      },
      departments: [],
      allSpecializations: [],
      selectedSpecializations: [],
      selectedSpecId: '',
      loading: false,
      submitting: false,
      errorMessage: null,
      errors: {}
    }
  },
  computed: {
    isEditing() {
      return !!this.id;
    },
    availableSpecializations() {
      // Filter out already selected specializations
      return this.allSpecializations.filter(spec =>
        !this.selectedSpecializations.some(selected => selected.id === spec.id)
      );
    }
  },
  methods: {
    async fetchDepartments() {
      try {
        const response = await this.$api.get('/departments');
        this.departments = response.data.data;
      } catch (error) {
        console.error('Error fetching departments:', error);
        this.errorMessage = 'Failed to load departments. Please try again.';
      }
    },
    async fetchSpecializations() {
      try {
        const response = await this.$api.get('/specializations');
        this.allSpecializations = response.data.data;
      } catch (error) {
        console.error('Error fetching specializations:', error);
        this.errorMessage = 'Failed to load specializations. Please try again.';
      }
    },
    async fetchNurseData() {
      if (!this.isEditing) return;
      
      this.loading = true;
      try {
        const response = await this.$api.get(`/nurses/${this.id}`);
        const nurseData = response.data;
        
        // Format joining_date correctly for the date input (YYYY-MM-DD)
        let formattedJoiningDate = '';
        if (nurseData.joining_date) {
          const date = new Date(nurseData.joining_date);
          formattedJoiningDate = date.toISOString().split('T')[0];
        }
        
        // Map returned data to form fields
        this.nurseData = {
          name: nurseData.user.name,
          email: nurseData.user.email,
          department_id: nurseData.department_id,
          joining_date: formattedJoiningDate,
          max_hours_per_week: nurseData.max_hours_per_week || 40,
          min_rest_hours: nurseData.min_rest_hours || 10,
          is_active: nurseData.user.is_active === 1 || nurseData.user.is_active === true,
          notes: nurseData.notes || '',
          mobile_number: nurseData.mobile_number || '',
          hourly_rate: nurseData.hourly_rate || ''
        };
        
        console.log('Nurse status:', nurseData.user.is_active);
        console.log('Parsed status:', this.nurseData.is_active);
        
        // Load nurse specializations
        if (nurseData.specializations) {
          this.selectedSpecializations = [...nurseData.specializations];
        }
      } catch (error) {
        console.error('Error fetching nurse details:', error);
        this.errorMessage = 'Failed to load nurse data. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    addSpecialization() {
      if (!this.selectedSpecId) return;
      
      const specToAdd = this.allSpecializations.find(spec => spec.id == this.selectedSpecId);
      if (specToAdd) {
        this.selectedSpecializations.push(specToAdd);
        this.selectedSpecId = '';
      }
    },
    removeSpecialization(specId) {
      this.selectedSpecializations = this.selectedSpecializations.filter(spec => spec.id !== specId);
    },
    async submitForm() {
      this.submitting = true;
      this.errors = {};
      this.errorMessage = null;
      
      try {
        const formData = {
          ...this.nurseData,
          specialization_ids: this.selectedSpecializations.map(spec => spec.id)
        };
        
        console.log('Submitting nurse data:', formData);
        
        // let response;
        if (this.isEditing) {
          console.log(`Updating nurse ${this.id} with is_active:`, formData.is_active);
          const response = await this.$api.put(`/nurses/${this.id}`, formData);
          console.log('Update response:', response.data);
        } else {
          await this.$api.post('/nurses', formData);
        }
        
        this.$toast.success(`Nurse ${this.isEditing ? 'updated' : 'created'} successfully`);
        this.goBack();
      } catch (error) {
        console.error('Error submitting form:', error);
        
        if (error.response && error.response.status === 422) {
          // Validation errors
          this.errors = error.response.data.errors || {};
          this.errorMessage = 'Please correct the errors below.';
        } else {
          this.errorMessage = `Failed to ${this.isEditing ? 'update' : 'create'} nurse. Please try again.`;
        }
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      this.$router.push({name: 'nurses'});
    }
  },
  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.fetchDepartments(),
        this.fetchSpecializations()
      ]);
      
      if (this.isEditing) {
        await this.fetchNurseData();
      }
    } catch (error) {
      console.error('Error initializing form:', error);
      this.errorMessage = 'Failed to initialize form. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style scoped>
.nurse-form-container {
  padding: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.form-container {
  max-width: 900px;
  margin: 0 auto;
}

.card {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 8px;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.required {
  color: red;
}

.specialization-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.spec-chip {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  border-radius: 16px;
  padding: 5px 10px;
  font-size: 14px;
}

.btn-remove {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 16px;
  margin-left: 5px;
  cursor: pointer;
  padding: 0 3px;
  line-height: 1;
}

.btn-remove:hover {
  color: #dc3545;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>